import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, Mail, Shield, Building2, Users, Eye, EyeOff, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { AvatarUploadDialog } from '../components/AvatarUploadDialog';
import { avatarSrc } from '../lib/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  gestor: 'Gestor',
  admin: 'Admin',
  parceiro: 'Parceiro',
  backoffice: 'Backoffice',
  comercial: 'Comercial',
};

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [photoOpen, setPhotoOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('As passwords não coincidem');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('A nova password deve ter pelo menos 6 caracteres');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password alterada com sucesso');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao alterar password';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
        <h1 className="text-2xl font-bold text-slate-800">O Meu Perfil</h1>

        {/* Profile Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-[#2D3E6F]" />
              Informações
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4" data-testid="profile-photo-block">
              <Avatar className="h-20 w-20 border-4 border-[#4DD4E8]/30">
                <AvatarImage src={avatarSrc(user?.avatar_url)} className="object-cover" />
                <AvatarFallback className="bg-[#4DD4E8] text-white text-2xl font-semibold">{(user?.name || '?').charAt(0)}</AvatarFallback>
              </Avatar>
              <Button type="button" variant="outline" onClick={() => setPhotoOpen(true)} className="gap-2" data-testid="profile-photo-btn"><Camera className="w-4 h-4" />{user?.avatar_url ? 'Mudar foto' : 'Carregar foto'}</Button>
            </div>
            <AvatarUploadDialog open={photoOpen} onClose={() => setPhotoOpen(false)} userId={user?.id} name={user?.name} currentUrl={user?.avatar_url} onSaved={() => refreshUser()} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Nome</Label>
                <p className="text-sm font-medium text-slate-800 mt-1">{user?.name}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                <p className="text-sm font-medium text-slate-800 mt-1">{user?.email}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1"><Shield className="w-3 h-3" /> Função</Label>
                <p className="text-sm font-medium text-slate-800 mt-1">{ROLE_LABELS[user?.role] || user?.role}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Parceiro</Label>
                <p className="text-sm font-medium text-slate-800 mt-1">{user?.partner_name || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#2D3E6F]" />
              Alterar Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label>Password atual</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    data-testid="current-password-input"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Nova password</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowNew(!showNew)}>
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirmar nova password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="mt-1.5"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button type="submit" disabled={saving} className="bg-[#2D3E6F] hover:bg-[#1e2d52]" data-testid="change-password-button">
                {saving ? 'A guardar...' : 'Alterar Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
